import React, { useState } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import {
  ArrowRight, RocketLaunch, Database, ChartLineUp, CaretDown,
  BookOpen, Cpu, ShieldCheck, Pulse, X, TrendUp, Lightning,
  TelegramLogo, WhatsappLogo
} from "@phosphor-icons/react";
import ArbitrageTicker from "@/components/ArbitrageTicker";
import LiveBotTerminal from "@/components/LiveBotTerminal";

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-800 group bg-slate-900/20 hover:bg-slate-900/40 transition-colors px-6 rounded-2xl mb-4">
      <button 
        onClick={() => setOpen(!open)} 
        className="w-full py-6 flex justify-between items-center text-left"
      >
        <span className="font-bold text-lg text-slate-200 group-hover:text-indigo-400 transition-colors pr-4">{q}</span>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${open ? 'bg-indigo-500/20 text-indigo-400' : 'bg-slate-800 text-slate-400'}`}>
          <CaretDown size={16} className={`transform transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${open ? 'max-h-96 opacity-100 pb-6' : 'max-h-0 opacity-0'}`}>
        <p className="text-slate-300 leading-relaxed text-sm sm:text-base pr-8 whitespace-pre-wrap">
          {a}
        </p>
      </div>
    </div>
  );
};

export default function Landing() {
  const faqs = [
    {
      q: "What is B3eye?",
      a: "B3eye is a showcase of institutional-grade algorithmic trading infrastructure built specifically for the Solana blockchain. It demonstrates four distinct, fully autonomous trading models: Arbitrage, Pump Sniper, AI Momentum, and AI Synergy. Our focus is on exploiting the unique latency and low-fee characteristics of Solana to capture micro-inefficiencies in the market."
    },
    {
      q: "Why does B3eye operate exclusively on Solana?",
      a: "Solana's 400-millisecond block time and deterministic state access allow for execution speeds that are impossible on EVM chains like Ethereum (which has 12-second block times). This sub-second latency is critical for strategies like Block-0 liquidity sniping and cross-exchange atomic arbitrage, where opportunities exist for less than a second."
    },
    {
      q: "How does the Arbitrage bot execute without directional risk?",
      a: "The module leverages atomic transactions and Jito bundles. It continuously monitors order books across centralized exchanges and Solana DEXs. When a spread exceeds the network fee threshold, it constructs a multi-hop routing instruction (e.g., Buy on Orca, Sell on Raydium). Because Solana processes these instructions atomically within a single transaction, if the spread collapses before finalization, the entire transaction reverts, costing only a fraction of a cent."
    },
    {
      q: "What is 'Block 0 Sniping' in the Pump Sniper module?",
      a: "When a token hits the required ~69k USD market cap on Pump.fun, its bonding curve completes, and its liquidity is automatically transferred to a new Raydium AMM pool. Our bot monitors the WebSocket stream to detect this exact state change. It then submits a heavily tipped transaction via dedicated RPCs to ensure our buy order is included in the very first block of the new pool, front-running retail discovery."
    },
    {
      q: "What data drives the AI Momentum bot?",
      a: "The AI module ingests a continuous stream of tick-by-tick order flow, deep order book imbalances, and pending mempool transactions. It calculates macroscopic indicators (RSI, MACD, Bollinger Bands) natively on-chain. When confidence thresholds exceed optimal levels (typically >84%), it executes predictive swing trades strictly on high-liquidity SOL/USDC pairs."
    },
    {
      q: "How does the AI Synergy orchestrator work?",
      a: "The Synergy Engine acts as a master node running a real-time risk parity algorithm. It constantly evaluates global market volatility and liquidity depth. During extreme volatility, it aggressively allocates capital toward Momentum and Migration modules. During choppy, sideways price action, it hunts micro-spreads via the Arbitrage module, ensuring optimal capital efficiency."
    },
    {
      q: "What are Jito Bundles and why are they used?",
      a: "Jito bundles allow searchers (bots) to bypass the public mempool and send transactions directly to the block leader (validator). By appending a small SOL 'tip' to the transaction, we incentivize the validator to include our sequence exactly as ordered. This completely eliminates the risk of MEV sandwich attacks or front-running by competing bots."
    },
    {
      q: "How is Dynamic Slippage managed?",
      a: "Static slippage is highly dangerous in volatile environments. If it's too low, transactions fail; if too high, bots extract the difference. Our Dynamic Slippage Engine reads real-time network congestion and AMM pool depth to adjust slippage on a per-transaction basis, optimizing execution success while minimizing value leakage."
    },
    {
      q: "Are the metrics displayed real?",
      a: "This is a demonstration environment. The SOL/USD pricing, the 11-exchange price aggregate, and the Pump.fun migration radar are fed by real live data. The bot execution logs, specific strategy PnLs, and the Synergy allocation percentages are simulated algorithms designed to showcase how the backend architecture would process these real-world data points."
    },
    {
      q: "What is the Cross-DEX Matrix Framework?",
      a: "Liquidity on Solana is fragmented across Raydium, Orca, Meteora, and Phoenix. Our Matrix Framework maintains a normalized, real-time graph of all these liquidity pools. It uses a modified Bellman-Ford algorithm to find the most profitable routing path across multiple pools in under 10 milliseconds."
    }
  ];

  const [news, setNews] = useState([]);
  const [loadingNews, setLoadingNews] = useState(true);

  React.useEffect(() => {
    const fetchNews = async () => {
      try {
        const [ct, btc, slate] = await Promise.all([
          fetch("https://api.rss2json.com/v1/api.json?rss_url=https://cointelegraph.com/rss").then(r => r.json()).catch(() => ({items: []})),
          fetch("https://api.rss2json.com/v1/api.json?rss_url=https://www.newsbtc.com/feed/").then(r => r.json()).catch(() => ({items: []})),
          fetch("https://api.rss2json.com/v1/api.json?rss_url=https://cryptoslate.com/feed/").then(r => r.json()).catch(() => ({items: []}))
        ]);
        
        let allNews = [];
        if (ct && ct.items) allNews = [...allNews, ...ct.items.map(i => ({...i, source: 'Cointelegraph'}))];
        if (btc && btc.items) allNews = [...allNews, ...btc.items.map(i => ({...i, source: 'NewsBTC'}))];
        if (slate && slate.items) allNews = [...allNews, ...slate.items.map(i => ({...i, source: 'CryptoSlate'}))];

        allNews.sort((a, b) => new Date(b.pubDate) - new Date(a.pubDate));
        setNews(allNews);
      } catch (err) {
        // silently ignore
      } finally {
        setLoadingNews(false);
      }
    };
    fetchNews();
  }, []);

  const varsityData = [
    {
      tag: "Arbitrage",
      title: "Atomic Routing Basics",
      desc: "An introduction to Solana's atomic routing mechanism. Explores how fragmented liquidity pools allow for risk-free trades without relying on the mempool.",
      profit: "+0.05% to +0.2% per tx",
      image: "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&w=600&q=85",
      color: "#6366f1",
      profitColor: "#34d399",
      detailedContent: "Atomic routing is the backbone of risk-free MEV on Solana. Because Solana processes transactions in parallel, executing multiple instructions sequentially within a single transaction ensures that either the entire sequence succeeds, or it fails entirely without costing capital. This module identifies pricing discrepancies between decentralized exchanges and centralized order books. By wrapping buys, transfers, and sells in a single atomic bundle, we guarantee that the profit is locked in before the block is finalized."
    },
    {
      tag: "MEV",
      title: "Jito Bundle Engineering",
      desc: "Deep dive into avoiding sandwich attacks. Utilizing Jito tip mechanics to guarantee that your transaction series remains atomic and invisible to public RPCs.",
      profit: "~1.5% daily compound",
      image: "https://images.unsplash.com/photo-1727434032773-af3cd98375ba?crop=entropy&cs=srgb&fm=jpg&w=600&q=85",
      color: "#8b5cf6",
      profitColor: "#34d399",
      detailedContent: "Jito bundles allow searchers to bypass the public mempool and send transactions directly to validators. By appending a small SOL 'tip' to the transaction bundle, we incentivize the validator to include our transaction sequence exactly as ordered. This effectively eliminates the risk of sandwich attacks or front-running by competing bots. Our Jito integration dynamically calculates the minimum required tip to ensure block inclusion while maximizing net profit, optimizing the MEV extraction process down to the micro-lamport level."
    },
    {
      tag: "Pump.fun",
      title: "Bonding Curve Mathematics",
      desc: "Decoding the mathematical model of Pump.fun tokens. Explains how the $69k target is calculated and verified exactly two blocks prior to graduation.",
      profit: "300% - 1000% per snipe",
      image: "https://images.unsplash.com/photo-1545987796-200677ee1011?crop=entropy&cs=srgb&fm=jpg&w=600&q=85",
      color: "#f43f5e",
      profitColor: "#2dd4bf",
      detailedContent: "Pump.fun utilizes a deterministic bonding curve to price tokens dynamically as supply decreases. The curve is mathematically structured so that exactly 85 SOL must be deposited to purchase the circulating supply required for graduation. Our module constantly evaluates the mathematical derivative of this curve. By tracking the exact micro-lamport distance to the 100% completion threshold, the bot can predict the exact block where the token will migrate to PumpSwap, allowing it to pre-position execution logic."
    },
    {
      tag: "Execution",
      title: "Block 0 Liquidity Sniping",
      desc: "The art of being the absolute first buyer in a newly seeded Raydium pool. Understanding priority fees, compute units, and node latency.",
      profit: "Up to +40% spread",
      image: "https://images.unsplash.com/photo-1644325349124-d1756b79dd42?crop=entropy&cs=srgb&fm=jpg&w=600&q=85",
      color: "#0ea5e9",
      profitColor: "#2dd4bf",
      detailedContent: "When a token migrates from Pump.fun to Raydium (PumpSwap), initial liquidity is deposited into a new AMM pool. The first buyers in this new pool acquire tokens at the absolute lowest baseline price before retail discovery. Block 0 sniping requires incredibly low latency. We utilize dedicated, geographically optimized RPC nodes to spam buy-instructions the millisecond the create_pool instruction is detected. This allows the module to acquire significant token volume and ride the initial price discovery wave for massive percentage gains."
    },
    {
      tag: "Machine Learning",
      title: "Predictive Order Flow AI",
      desc: "Ingesting raw tick data to train momentum models. How AI anticipates retail buying pressure by calculating localized RSI and MACD.",
      profit: "+5% to +12% swing capture",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&w=600&q=85",
      color: "#10b981",
      profitColor: "#10b981",
      detailedContent: "Rather than reacting to price action, this AI model anticipates it. By streaming live mempool data and mapping pending transactions, the neural network identifies large impending buys or sells before they are confirmed on-chain. It cross-references this with real-time RSI and MACD indicators to filter out noise. When a high-probability vector is detected, the bot automatically front-runs or tailgates the momentum, capturing the resulting price swing."
    },
    {
      tag: "Network",
      title: "The 400ms Solana Advantage",
      desc: "Comparing Solana execution speeds to Ethereum. How the 400ms block time unlocks unique, highly-profitable pathways exclusive to this architecture.",
      profit: "5x more opportunities",
      image: "https://images.unsplash.com/photo-1518546305927-5a555bb7020d?crop=entropy&cs=srgb&fm=jpg&w=600&q=85",
      color: "#f59e0b",
      profitColor: "#34d399",
      detailedContent: "Solana's 400-millisecond block time fundamentally changes algorithmic trading. Unlike Ethereum's 12-second blocks, Solana allows for near-instantaneous price discovery and arbitrage execution. However, this speed requires highly optimized code. Our infrastructure is written to minimize compute-unit (CU) consumption, ensuring our transactions are processed faster than bloated retail swaps. This latency advantage means we can capture fleeting micro-spreads that exist for less than half a second."
    },
    {
      tag: "Risk Management",
      title: "Dynamic Slippage Engines",
      desc: "Safeguarding automated trades from impermanent loss. Our dynamic engine adjusts slippage tolerances on the fly based on current RPC congestion.",
      profit: "Saves ~3% execution cost",
      image: "https://images.unsplash.com/photo-1605792657660-596af9009e82?crop=entropy&cs=srgb&fm=jpg&w=600&q=85",
      color: "#8b5cf6",
      profitColor: "#10b981",
      detailedContent: "Static slippage is a death sentence in high-volatility environments. If slippage is too low, transactions fail; if too high, MEV bots sandwich the trade, stealing the profit. Our Dynamic Slippage Engine reads real-time network congestion and pool volatility to adjust slippage on a per-transaction basis. During stable periods, it tightens slippage to 0.1% to preserve capital. During violent pump migrations, it widens to ensure execution while deploying Jito bundles to prevent sandwiching."
    },
    {
      tag: "Synergy",
      title: "AI Risk Parity Orchestration",
      desc: "The logic behind the Master Node. Dynamically shifting capital allocation between Arbitrage, Migration, and Momentum strategies based on macro signals.",
      profit: "+8% to +15% weekly average",
      image: "https://images.unsplash.com/photo-1694903110330-cc64b7e1d21d?crop=entropy&cs=srgb&fm=jpg&w=600&q=85",
      color: "#6366f1",
      profitColor: "#2dd4bf",
      detailedContent: "Capital efficiency is maximized when funds are constantly deployed in the most profitable module. The Synergy Master Node evaluates the global market state—measuring volume, volatility, and DEX liquidity depth. In a sideways market, it routes 80% of resources to the Arbitrage module for slow, steady accumulation. When a new meta narrative launches and Pump.fun volume spikes, it instantly reallocates capital to the Migration Sniper. This risk-parity approach ensures consistent compounding regardless of market conditions."
    },
    {
      tag: "Monitoring",
      title: "Cross-DEX Matrix Framework",
      desc: "How we aggregate live data feeds from 11 CEXs and all major Solana AMMs. Managing API rate limits, WebSocket disconnects, and normalized pairs.",
      profit: "+0.4% average spread",
      image: "https://images.unsplash.com/photo-1674027444485-cec3da58eef4?crop=entropy&cs=srgb&fm=jpg&w=600&q=85",
      color: "#f43f5e",
      profitColor: "#34d399",
      detailedContent: "Liquidity on Solana is heavily fragmented across platforms like Raydium, Orca, Meteora, and Phoenix. Our Cross-DEX Matrix Framework maintains a normalized, real-time graph of all liquidity pools. It uses a modified Bellman-Ford algorithm to find the most profitable routing path across multiple pools in under 10 milliseconds. This ensures that even large order sizes suffer minimal price impact, executing across 3 or 4 different AMMs simultaneously to achieve the best average entry or exit price."
    },
    {
      tag: "Momentum",
      title: "Whale Wallet Tracking",
      desc: "Filtering the noise. Identifying and mapping institutional-grade SOL movements to front-run massive volatility spikes before they propagate.",
      profit: "Captures +10% macro swings",
      image: "https://images.unsplash.com/photo-1697577418970-95d99b5a55cf?crop=entropy&cs=srgb&fm=jpg&w=600&q=85",
      color: "#0ea5e9",
      profitColor: "#2dd4bf",
      detailedContent: "The blockchain is fully transparent, meaning institutional and 'whale' movements can be tracked in real-time. This module monitors thousands of known high-net-worth and profitable trader wallets. When a cluster of these wallets begins accumulating a specific asset or interacting with a new contract, the bot flags it as a momentum anomaly. By tailgating these smart-money movements, the platform captures macro swings early, riding the institutional wave before it hits mainstream crypto news feeds."
    }
  ];

  const [selectedVarsity, setSelectedVarsity] = useState(null);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 overflow-x-hidden selection:bg-indigo-500/30 selection:text-indigo-200 font-sans">
      <ArbitrageTicker />

      {/* HERO SECTION */}
      <section className="relative z-10 pt-24 pb-20 lg:pt-32 lg:pb-28 px-6 max-w-[1500px] mx-auto text-center">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-500/20 blur-[120px] rounded-full pointer-events-none z-0" />
        
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-indigo-500/30 bg-indigo-500/10 text-indigo-400 text-xs font-semibold tracking-wide mb-8">
            <span className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse" />
            Live Demonstration Environment
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight leading-[1.1] mb-6 text-slate-100">
            The Bot&apos;s Eye <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
              Never Misses The Profit
            </span>
          </h1>
          <p className="text-slate-300 text-lg sm:text-xl leading-relaxed mb-10 max-w-2xl mx-auto">
            B3eye deploys sub-millisecond algorithmic models directly on the Solana network. Monitor live MEV Arbitrage, Migration Sniping, and Predictive AI Momentum in real-time.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/dashboard" className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-400 hover:to-purple-500 text-white rounded-full px-8 py-4 font-bold text-sm transition-all shadow-lg shadow-indigo-500/25 flex items-center justify-center gap-2 group">
              Open Command Center <ArrowRight weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/tape" className="bg-slate-900 border border-slate-700 hover:bg-slate-800 text-slate-200 rounded-full px-8 py-4 font-bold text-sm transition-all flex items-center justify-center gap-2 group">
              View Live Tape <Pulse className="text-indigo-400" />
            </Link>
          </div>
        </div>
      </section>

      {/* TERMINAL SHOWCASE */}
      <section className="relative z-10 px-6 pb-24 max-w-[1200px] mx-auto">
         <div className="h-[350px] sm:h-[450px] w-full rounded-2xl overflow-hidden shadow-2xl shadow-indigo-900/20 border border-slate-800 bg-slate-900">
            <LiveBotTerminal maxItems={30} />
         </div>
      </section>

      {/* THE 4 BOTS / FEATURES */}
      <section className="relative z-10 py-24 bg-slate-900/30 border-y border-slate-800 backdrop-blur-xl">
        <div className="max-w-[1500px] mx-auto px-6">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-100 mb-4">Algorithmic Infrastructure</h2>
            <p className="text-slate-300 text-base leading-relaxed">
              Our architecture is divided into four distinct algorithmic modules, each highly optimized for specific market conditions and latency requirements.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            <Link to="/bot/arbitrage" className="p-8 rounded-2xl border border-slate-800 bg-slate-900/50 hover:bg-slate-800/80 hover:border-indigo-500/50 transition-all duration-300 group">
              <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-indigo-500/20 transition-all">
                <TrendUp size={24} className="text-indigo-400" />
              </div>
              <h3 className="text-2xl font-bold text-slate-100 mb-3">Cross-Exchange Arbitrage</h3>
              <p className="text-slate-300 text-sm leading-relaxed mb-6">
                Monitors liquidity discrepancies across primary centralized venues and Solana automated market makers. Executes atomic routing to secure basis point spreads without market exposure.
              </p>
              <span className="text-indigo-400 font-semibold text-sm group-hover:pl-2 transition-all flex items-center gap-1">Explore Module <ArrowRight size={14} weight="bold" /></span>
            </Link>

            <Link to="/bot/pump-migration" className="p-8 rounded-2xl border border-slate-800 bg-slate-900/50 hover:bg-slate-800/80 hover:border-teal-500/50 transition-all duration-300 group">
              <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-teal-500/20 transition-all">
                <Database size={24} className="text-teal-400" />
              </div>
              <h3 className="text-2xl font-bold text-slate-100 mb-3">Pump Sniper</h3>
              <p className="text-slate-300 text-sm leading-relaxed mb-6">
                Listens to real-time WebSockets to pinpoint when bonding curves reach 100% capacity. Instantly acquires liquidity block-0 upon Raydium migration.
              </p>
              <span className="text-teal-400 font-semibold text-sm group-hover:pl-2 transition-all flex items-center gap-1">Explore Module <ArrowRight size={14} weight="bold" /></span>
            </Link>

            <Link to="/bot/ai-trade" className="p-8 rounded-2xl border border-slate-800 bg-slate-900/50 hover:bg-slate-800/80 hover:border-rose-500/50 transition-all duration-300 group">
              <div className="w-12 h-12 rounded-xl bg-rose-500/10 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-rose-500/20 transition-all">
                <ChartLineUp size={24} className="text-rose-400" />
              </div>
              <h3 className="text-2xl font-bold text-slate-100 mb-3">AI Momentum Trading</h3>
              <p className="text-slate-300 text-sm leading-relaxed mb-6">
                Processes live mempool transactions and order flow imbalances. Predicts immediate price direction on major SOL pairs and rides institutional momentum waves.
              </p>
              <span className="text-rose-400 font-semibold text-sm group-hover:pl-2 transition-all flex items-center gap-1">Explore Module <ArrowRight size={14} weight="bold" /></span>
            </Link>

            <Link to="/bot/ai-combo" className="p-8 rounded-2xl border border-slate-700 bg-gradient-to-br from-slate-800 to-slate-900 hover:border-purple-400 transition-all duration-300 group shadow-lg shadow-purple-900/10">
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-all">
                <Cpu size={24} className="text-purple-300" />
              </div>
              <h3 className="text-2xl font-bold text-slate-100 mb-3">AI Synergy Engine</h3>
              <p className="text-slate-300 text-sm leading-relaxed mb-6">
                The autonomous routing core. Continuously evaluates macro market conditions to distribute execution weight across Arbitrage, Sniping, and Momentum algorithms.
              </p>
              <span className="text-purple-300 font-semibold text-sm group-hover:pl-2 transition-all flex items-center gap-1">Explore Console <ArrowRight size={14} weight="bold" /></span>
            </Link>
          </div>
        </div>
      </section>

      {/* VARSITY SECTION */}
      <section className="relative z-10 py-24 max-w-[1500px] mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-100 mb-2">Research & Analytics</h2>
            <p className="text-slate-300">Technical documentation on our execution environment.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {varsityData.map((art, i) => (
            <div onClick={() => setSelectedVarsity(art)} key={i} className="flex flex-col group cursor-pointer h-full border border-slate-800 bg-slate-900/50 hover:bg-slate-800 hover:border-[color:var(--c)] transition-all rounded-2xl p-4 relative overflow-hidden shadow-sm hover:shadow-lg" style={{"--c": art.color}}>
              <div className="h-32 w-full bg-slate-950 border border-slate-800 mb-4 flex items-center justify-center relative overflow-hidden rounded-xl">
                <img src={art.image} alt={art.title} className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
              </div>
              
              <span className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: art.color }}>{art.tag}</span>
              <h3 className="text-lg font-bold text-slate-200 mb-2 group-hover:text-white transition-colors line-clamp-2 leading-tight">{art.title}</h3>
              <p className="text-slate-300 text-xs leading-relaxed mb-4 flex-1 line-clamp-4">{art.desc}</p>
              
              <div className="mt-auto border-t border-slate-800 pt-3">
                <div className="text-[10px] text-slate-500 mb-1 font-medium">Estimated Potential</div>
                <div className="text-sm font-bold" style={{ color: art.profitColor }}>{art.profit}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* VARSITY MODAL */}
      {selectedVarsity && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-md" onClick={() => setSelectedVarsity(null)} />
          <div className="relative bg-slate-900 border border-slate-700 max-w-2xl w-full rounded-2xl shadow-2xl shadow-black flex flex-col max-h-[90vh] overflow-hidden">
            <button 
              onClick={() => setSelectedVarsity(null)}
              className="absolute top-4 right-4 text-slate-300 hover:text-white z-10 bg-slate-950/50 p-2 rounded-full backdrop-blur-md border border-slate-700 transition-colors"
            >
              <X size={20} />
            </button>
            <div className="h-48 sm:h-64 w-full relative shrink-0">
               <img src={selectedVarsity.image} alt={selectedVarsity.title} className="w-full h-full object-cover opacity-80" />
               <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent" />
            </div>
            <div className="p-6 sm:p-8 overflow-y-auto custom-scrollbar flex-1">
               <span className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: selectedVarsity.color }}>{selectedVarsity.tag}</span>
               <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-100 mb-6">{selectedVarsity.title}</h2>
               <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-8">
                 {selectedVarsity.detailedContent}
               </p>
               <div className="border border-slate-800 bg-slate-950/50 rounded-xl p-5 flex items-center justify-between">
                 <span className="text-xs font-medium text-slate-400">Estimated Strategy Potential</span>
                 <span className="font-bold text-lg" style={{ color: selectedVarsity.profitColor }}>{selectedVarsity.profit}</span>
               </div>
            </div>
          </div>
        </div>
      )}

      {/* FAQ SECTION */}
      <section className="relative z-10 py-24 bg-slate-900/30 border-t border-slate-800">
        <div className="max-w-[800px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-100 mb-2">Frequently Asked Questions</h2>
            <p className="text-slate-300">Technical insights into our infrastructure.</p>
          </div>
          
          <div className="space-y-2">
            {faqs.map((faq, i) => (
              <FaqItem key={i} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* LATEST CRYPTO NEWS TICKER (Horizontal) */}
      {news.length > 3 && (
        <div className="relative z-10 border-y border-slate-800 bg-slate-900 py-3">
          <div className="flex items-center">
            <div className="px-6 border-r border-slate-700 shrink-0 flex items-center gap-2 hidden md:flex">
               <span className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse" />
               <span className="text-slate-300 text-xs font-semibold uppercase tracking-wider">Global Feed</span>
            </div>
            <div className="flex-1 overflow-hidden md:ml-4">
              <Marquee speed={40} gradient={false} pauseOnHover>
                {news.slice(3, 28).map((item, i) => {
                  const date = new Date(item.pubDate);
                  const utcTime = `${date.getUTCHours().toString().padStart(2, '0')}:${date.getUTCMinutes().toString().padStart(2, '0')} UTC`;
                  return (
                    <div key={i} className="flex items-center gap-3 mx-6 text-sm opacity-80 hover:opacity-100 transition-opacity">
                      <span className="text-indigo-400 font-mono text-[10px] font-semibold">{utcTime}</span>
                      <span className="text-slate-400 text-[10px] uppercase font-bold border border-slate-700 px-2 py-0.5 rounded-md bg-slate-800">{item.source}</span>
                      <span className="text-slate-200 tracking-wide whitespace-nowrap">{item.title}</span>
                      <span className="text-slate-600 ml-6 mr-2">•</span>
                    </div>
                  );
                })}
              </Marquee>
            </div>
          </div>
        </div>
      )}

      {/* CTA / FOOTER */}
      <footer className="relative z-10 bg-slate-950 pt-20 pb-10">
        <div className="max-w-[1500px] mx-auto px-6 text-center mb-20">
          <div className="w-16 h-16 mx-auto bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mb-8 shadow-lg shadow-indigo-500/20">
            <RocketLaunch size={32} color="#FFF" weight="fill" />
          </div>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-slate-100 mb-6">Initialize Terminal</h2>
          <p className="text-slate-300 max-w-2xl mx-auto mb-10 text-lg">
            Gain read-only access to our live execution environment and monitor algorithmic strategies directly on the Solana mainnet.
          </p>
          <Link to="/dashboard" className="bg-slate-100 hover:bg-white text-slate-900 rounded-full inline-flex items-center gap-2 px-8 py-4 font-bold transition-all shadow-lg">
            Open Command Center <Lightning weight="fill" />
          </Link>
        </div>

        <div className="max-w-[1500px] mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6 text-sm text-slate-500 font-medium border-t border-slate-800 pt-10">
          <div>© 2026 B3eye Infrastructure</div>
          <div className="flex items-center gap-6">
            <a
              href="https://t.me/b3eyeofficial"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 hover:text-sky-400 transition-colors"
            >
              <TelegramLogo size={18} weight="fill" className="text-sky-400" /> Telegram Channel
            </a>
            <a
              href="https://whatsapp.com/channel/0029VbDffk1545upmKKQ9Y3D"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 hover:text-emerald-400 transition-colors"
            >
              <WhatsappLogo size={18} weight="fill" className="text-emerald-400" /> WhatsApp Channel
            </a>
          </div>
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-indigo-400" /> Secure Frontend Architecture
          </div>
        </div>
      </footer>
    </div>
  );
}
